import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Card,
  CardContent,
} from '@mui/material';
import PaymentsIcon from '@mui/icons-material/Payments';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingIcon from '@mui/icons-material/Pending';
import { adminAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import PortalLayout from '../../components/PortalLayout';

const statusColor: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  completed: 'success',
  failed: 'error',
  pending: 'warning',
  refunded: 'default',
};

const statusLabel: Record<string, string> = {
  completed: 'Abgeschlossen',
  failed: 'Fehlgeschlagen',
  pending: 'Ausstehend',
  refunded: 'Erstattet',
};

const ITEMS_PER_PAGE = 20;

const AdminPaymentsPage: React.FC = () => {
  const [payments, setPayments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<any>(null);
  const { addToast } = useToast();

  const loadPayments = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: any = {
        limit: ITEMS_PER_PAGE,
        offset: (page - 1) * ITEMS_PER_PAGE,
      };
      if (statusFilter) params.status = statusFilter;
      const response = await adminAPI.getPayments(params);
      setPayments(response.data.data || []);
      setTotal(response.data.total || 0);
    } catch {
      addToast('Fehler beim Laden der Zahlungen', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [page, statusFilter, addToast]);

  const loadStats = useCallback(async () => {
    try {
      const response = await adminAPI.getStatistics();
      setStats(response.data);
    } catch {
    }
  }, []);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  return (
    <PortalLayout>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <PaymentsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Zahlungsübersicht
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary">
          Alle Zahlungstransaktionen im Überblick
        </Typography>
      </Box>

      {stats?.payments && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
          <Card sx={{ flex: 1, minWidth: 200 }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 36 }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  €{parseFloat(stats.payments.total_revenue || 0).toFixed(2)}
                </Typography>
                <Typography variant="body2" color="textSecondary">Gesamtumsatz</Typography>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1, minWidth: 200 }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PendingIcon sx={{ color: 'warning.main', fontSize: 36 }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {stats.payments.total_payments || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">Gesamttransaktionen</Typography>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1, minWidth: 200 }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 36 }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {stats.payments.completed_payments || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">Abgeschlossene Zahlungen</Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status filtern</InputLabel>
            <Select
              value={statusFilter}
              label="Status filtern"
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            >
              <MenuItem value="">Alle</MenuItem>
              <MenuItem value="completed">Abgeschlossen</MenuItem>
              <MenuItem value="pending">Ausstehend</MenuItem>
              <MenuItem value="failed">Fehlgeschlagen</MenuItem>
              <MenuItem value="refunded">Erstattet</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="textSecondary" sx={{ ml: 'auto' }}>
            {total} Zahlungen gesamt
          </Typography>
        </Box>
      </Paper>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : payments.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <PaymentsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="textSecondary">Keine Zahlungen gefunden</Typography>
        </Paper>
      ) : (
        <>
          <Table component={Paper} sx={{ mb: 3 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Benutzer</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Lead</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Betrag</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Datum</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Stripe-ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((payment) => (
                <TableRow key={payment.id} hover>
                  <TableCell>{payment.id}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {payment.user_name || '—'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {payment.user_email || ''}
                    </Typography>
                  </TableCell>
                  <TableCell>{payment.inquiry_title || '—'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                      €{parseFloat(payment.amount || 0).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={statusLabel[payment.status] || payment.status}
                      color={statusColor[payment.status] || 'default'}
                      size="small"
                      icon={
                        payment.status === 'completed' ? <CheckCircleIcon /> :
                        payment.status === 'failed' ? <CancelIcon /> :
                        <PendingIcon />
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {payment.created_at
                      ? new Date(payment.created_at).toLocaleDateString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
                      {payment.stripe_payment_intent_id
                        ? `${payment.stripe_payment_intent_id.substring(0, 20)}...`
                        : '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center">
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </PortalLayout>
  );
};

export default AdminPaymentsPage;
